import { notification } from 'antd';
import { Map } from 'ol';
import { Extent } from 'ol/extent';
import { isExtent } from './extent';
import { waitForMapToMoveEnd } from './map';

export const getMapCanvas = (map: Map): HTMLCanvasElement => {
  const mapCanvas = document.createElement('canvas');
  const size = map.getSize();

  if (typeof size === 'undefined') {
    throw new Error('Не удалось получить размеры карты');
  }
  const [width, height] = size;

  if (width === undefined || height === undefined) {
    throw new Error('Не удалось получить размеры карты');
  }

  mapCanvas.width = width;
  mapCanvas.height = height;
  const mapContext = mapCanvas.getContext('2d');

  document.querySelectorAll('.ol-layer canvas').forEach((canvasEl) => {
    const canvas = canvasEl as HTMLCanvasElement;
    if (canvas.width === 0) {
      return;
    }
    const parentEl = canvasEl.parentNode as HTMLElement;
    const opacity = parentEl.style.opacity;
    mapContext!.globalAlpha = opacity === '' ? 1 : Number(opacity);
    const transform = canvas.style.transform;
    const matrixArgs = transform
      .match(/^matrix\(([^\(]*)\)$/)![1]!
      .split(',')
      .map(Number);
    const matrix = new DOMMatrix(matrixArgs);
    mapContext?.setTransform(matrix);
    mapContext?.drawImage(canvas, 0, 0);
  });

  return mapCanvas;
};

export const getMapExtentCanvas = async (map: Map, extent: Extent) => {
  const mapView = map.getView();
  const originalCenter = mapView.getCenter();
  const originalResolution = mapView.getResolution();

  if (!isExtent(extent)) {
    throw new Error('Не удалось получить координаты экстента');
  }

  const [minExtentX, minExtentY, maxExtentX, maxExtentY] = extent;

  mapView.fit(extent, { size: map.getSize() });

  await waitForMapToMoveEnd(map);

  const canvas = getMapCanvas(map);

  const pixelTopLeft = map.getPixelFromCoordinate([minExtentX, maxExtentY]);
  const pixelBottomRight = map.getPixelFromCoordinate([maxExtentX, minExtentY]);

  if (!pixelTopLeft || !pixelBottomRight) {
    throw new Error('Не удалось получить координаты экстента');
  }

  const [minX, minY] = pixelTopLeft;
  const [maxX, maxY] = pixelBottomRight;

  if (
    minX === undefined ||
    minY === undefined ||
    maxX === undefined ||
    maxY === undefined
  ) {
    throw new Error('Не удалось получить координаты экстента');
  }

  const cropWidth = maxX - minX;
  const cropHeight = maxY - minY;

  // Обрезаем изображение до нужной области
  const croppedCanvas = document.createElement('canvas');
  croppedCanvas.width = cropWidth;
  croppedCanvas.height = cropHeight;
  const croppedContext = croppedCanvas.getContext('2d');

  croppedContext?.drawImage(
    canvas,
    minX,
    minY,
    cropWidth,
    cropHeight,
    0,
    0,
    cropWidth,
    cropHeight,
  );

  mapView.setCenter(originalCenter);
  mapView.setResolution(originalResolution);

  return croppedCanvas;
};

export const createMapScreenshot = async (map: Map, extent?: Extent) => {
  const promise = new Promise<string>((resolve, reject) => {
    map.once('rendercomplete', async () => {
      const mapCanvas = extent
        ? await getMapExtentCanvas(map, extent)
        : getMapCanvas(map);

      try {
        const screenshot = mapCanvas.toDataURL('image/png');
        resolve(screenshot);
      } catch (e) {
        if (e instanceof Error) {
          reject(new Error(e.message));
        }

        reject(new Error('Ошибка создания скриншота'));
      }
    });
  });

  map.renderSync();

  return promise;
};

export const printMapCanvas = (filename: string, map: Map) => {
  const { title } = document;
  const iframe = document.createElement('iframe');
  iframe.style.height = '0';
  iframe.style.visibility = 'hidden';
  iframe.style.width = '0';
  // firefox call this filename
  iframe.setAttribute(
    'srcdoc',
    `<html><head><title>${filename}</title></head><body></body></html>`,
  );

  const mapCanvas = getMapCanvas(map);
  const img = document.createElement('img');
  try {
    img.src = mapCanvas.toDataURL();
  } catch (e) {
    if (e instanceof Error) {
      notification.error({
        title: 'Не удалось распечатать карту',
        description: 'Карта не может быть экспортирована',
      });
      throw new Error(e.message);
    }
  }
  img.style.display = 'block';
  img.style.maxWidth = '100%';
  img.style.height = 'auto';
  img.style.marginLeft = 'auto';
  img.style.marginRight = 'auto';
  document.body.append(iframe);

  iframe.contentWindow?.addEventListener('afterprint', () => {
    iframe.parentElement?.removeChild(iframe);
    document.title = title;
  });

  iframe.addEventListener('load', () => {
    iframe.contentDocument?.body.append(img);
    // chrome call this filename
    document.title = filename;

    img.addEventListener('load', () => {
      iframe.contentWindow?.print();
    });
  });
};

export const getMapScreenshot = async (map: Map, extent?: Extent) => {
  try {
    return await createMapScreenshot(map, extent);
  } catch (e) {
    notification.error({
      title: 'Не удалось скачать скриншот',
      description: 'Карта не может быть экспортирована',
    });

    if (e instanceof Error) {
      throw new Error(e.message);
    }

    throw new Error('Ошибка создания скриншота');
  }
};
